import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import type {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { SessionExpiredProperties } from 'analytics/types';
import config from 'config';
import { sessionExpired, signOutUser, updateAccessToken } from 'redux/slices/user.slice';
import { RootState } from 'redux/store';
import { revokeFcmOnForcedSignOut } from 'services/fcmTokenStore';
import { withRefreshLock } from 'services/session/refreshLock';
import { adopted } from 'services/session/sessionMiddleware';
import { isSwitchPending, readSession } from 'services/session/sessionStore';

const REDUCER_PATH = 'api';
const REFRESH_URL = '/auth/refresh';

type QueryArgs = string | FetchArgs;
type RefreshSessionResponse = { accessToken: string };
type AppBaseQuery = BaseQueryFn<QueryArgs, unknown, FetchBaseQueryError>;
type RawBaseQuery = BaseQueryFn<QueryArgs, unknown, FetchBaseQueryError, Record<string, never>, FetchBaseQueryMeta>;

const rawBaseQuery = fetchBaseQuery({
  baseUrl: config.apiUrl,
  // Required so the httpOnly refreshToken cookie is sent with /auth/refresh and /auth/logout.
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    // Not changing headers for multipart/form-data
    if (headers.get('content-type')?.includes('multipart/form-data')) {
      return headers;
    }

    const { user } = getState() as RootState;
    if (user) {
      const { accessToken, permissionsHash } = user;
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }

      // Send the user's current permissions hash from the Redux state
      if (typeof permissionsHash === 'string') {
        headers.set('X-User-Permissions-Hash', permissionsHash);
      }
    }
    return headers;
  },
}) as RawBaseQuery;

const getRequestUrl = (args: QueryArgs): string => (typeof args === 'string' ? args : args.url);
const isAuthRequest = (args: QueryArgs): boolean => getRequestUrl(args).startsWith('/auth');
const isUnauthorizedError = (error: FetchBaseQueryError): boolean =>
  error.status === 401 || (error.status === 'PARSING_ERROR' && error.originalStatus === 401);

const forceSignOut = (api: BaseQueryApi, reason: SessionExpiredProperties['reason']): void => {
  api.dispatch(sessionExpired(reason));
  revokeFcmOnForcedSignOut().catch(() => {});
  api.dispatch({ type: `${REDUCER_PATH}/resetApiState` });
  api.dispatch(signOutUser());
};

let refreshRequest: Promise<string | null> | null = null;

const refreshSession = (api: BaseQueryApi, failedToken: string): Promise<string | null> => {
  if (!refreshRequest) {
    refreshRequest = withRefreshLock(failedToken, async (): Promise<string | null> => {
      const result = await rawBaseQuery({ url: REFRESH_URL, method: 'POST' }, api, {});
      return (result.data as RefreshSessionResponse | undefined)?.accessToken ?? null;
    }).finally((): void => {
      refreshRequest = null;
    });
  }

  return refreshRequest;
};

// Flow: run the original request, refresh only if it comes back 401, then retry that same request once.
const baseQueryWithReauth: AppBaseQuery = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  if (!result.error || !isUnauthorizedError(result.error) || isAuthRequest(args)) {
    return result;
  }

  if (isSwitchPending()) return result;

  const shared = readSession();
  if (!shared) {
    forceSignOut(api, 'missing_session');
    return result;
  }

  const { user } = api.getState() as RootState;
  if ((shared.projectId ?? null) !== (user.projectId ?? null)) return result;

  const accessToken = await refreshSession(api, user.accessToken);
  if (!accessToken) {
    forceSignOut(api, 'refresh_failed');
    return result;
  }

  api.dispatch(adopted(updateAccessToken(accessToken)));
  return rawBaseQuery(args, api, extraOptions);
};

// Flag to prevent multiple simultaneous invalidations
let isInvalidatingPermissions = false;

/**
 * BaseQuery with permissions change verification.
 */
export const baseQueryWithPermissionsCheck: AppBaseQuery = async (args, api, extraOptions) => {
  const result = await baseQueryWithReauth(args, api, extraOptions);
  const response = (result.meta as FetchBaseQueryMeta | undefined)?.response;
  const permissionsChanged = response?.headers.get('X-Permissions-Changed');

  if (permissionsChanged === 'true' && !isInvalidatingPermissions) {
    isInvalidatingPermissions = true;

    // Invalidates the permissions tag to force a refetch.
    // We use the action type string directly to avoid circular dependency with baseApi.
    api.dispatch({
      type: `${REDUCER_PATH}/invalidateTags`,
      payload: ['UserPermissions'],
    });

    // Reset flag after a short delay
    setTimeout(() => {
      isInvalidatingPermissions = false;
    }, 1000);
  }

  return result;
};

const baseApi = createApi({
  reducerPath: REDUCER_PATH,
  baseQuery: baseQueryWithPermissionsCheck,
  tagTypes: [
    'Messages',
    'DemoChatMessages',
    'Client',
    'PartnerProjects',
    'PartnerProperties',
    'PartnerWebhooks',
    'PartnerTeam',
    'PartnerTeamRoles',
    'PromptSections',
    'GlobalPromptSections',
    'GlobalPromptSectionHistory',
    'ClientNotes',
    'ClientDiscardReasons',
    'ExecutiveTask',
    'Notification',
    'ClientQuestion',
    'Alert',
    'Roles',
    'Role',
    'UserRoles',
    'Permissions',
    'Permission',
    'AssignmentRules',
    'AssignmentHistory',
    'User',
    'UserPermissions',
    'ScheduledTemplate',
    'TemplateBatch',
    'TemplateCatalog',
    'FunnelColumn',
    'ClientBulkImport',
    'SperantCRM',
    'WhatsAppCalls',
    'Brokerage',
    'BrokerageProfile',
    'MortgagePolicyEntities',
    'PartnerTickets',
    'PartnerCommercialConditions',
    'MortgageAnalysisReport',
    'WhatsAppFlow',
    'NotificationPreferences',
    'SourceMappingRule',
    'ExecutiveFiles',
  ] as const,
  endpoints: () => ({}),
});

export default baseApi;
